import React, { useEffect, useState, useMemo } from 'react';
import {
    Input,
    Select,
    Typography,
    Table,
    Tag,
    Button,
    Space,
    Card,
    Pagination,
    Row,
    Col,
    message,
    Tooltip
} from "antd";
import { useAppDispatch, useAppSelector } from "../../utils/hooks/redux";
import dayjs from "dayjs";
import relativeTime from 'dayjs/plugin/relativeTime';
import { Container } from "../Users/styles";
import { CustomersOptions, ActivityLogTypeValue, LostItemActivityLogText } from "../../types/ActivityLog";
import {
    MailOutlined,
    SearchOutlined,
    CalendarOutlined,
    CloseCircleOutlined,
    CheckCircleFilled
} from "@ant-design/icons";
import { getCustomerDetails, sendCustomerReminder } from "../../redux/features/customers/customerAsyncThunk";
import { customerListDetail } from "../../redux/features/customers/customerTypeSlice";
import { useDebounce } from "../../utils/hooks/useDebounce";
import { RequestResults } from "../../redux/common/RequestStatus";
import { useNavigate } from "react-router-dom";
import SystemPayRoutes from "../../utils/constants/Routes";
import DatePickerModal from "../../components/DatePickerModal";
import { getStatusTheme } from "../Reports/Board/statusThemes";
import {
    FilterTabWrapper,
    FilterButton,
    CountBadge,
    StatusFilters,
    FilterInputs,
    StyledSelect
} from "./styles";

dayjs.extend(relativeTime);

const { Text } = Typography;

type PaymentFilterKey = 'all' | 'paid' | 'never-paid' | 'refunded';

interface PaymentFilterOption {
    key: PaymentFilterKey;
    label: string;
    colors: {
        background: string;
        border: string;
        text: string;
    };
    countKey: 'counts' | 'paidCount' | 'neverPaidCount' | 'refundedCount';
}

const paymentFilterOptions: PaymentFilterOption[] = [
    {
        key: 'all',
        label: 'All',
        colors: { background: '#ffffff', border: '#d1d5db', text: '#1f2937' },
        countKey: 'counts'
    },
    {
        key: 'paid',
        label: 'Paid',
        colors: { background: '#dcfce7', border: '#22c55e', text: '#16a34a' },
        countKey: 'paidCount'
    },
    {
        key: 'never-paid',
        label: 'Never Paid',
        colors: { background: '#fef3c7', border: '#f59e0b', text: '#d97706' },
        countKey: 'neverPaidCount'
    },
    {
        key: 'refunded',
        label: 'Refunded',
        colors: { background: '#fee2e2', border: '#ef4444', text: '#dc2626' },
        countKey: 'refundedCount'
    }
];


const EMAIL_TYPE_OPTIONS = [
    { value: 'draft_incomplete_reminder', label: 'Draft Incomplete Reminder' },
    { value: 'draft_completed_reminder', label: 'Draft Completed Reminder' },
    { value: 'not_paid_reminder', label: 'Not Paid Reminder' },
    { value: 'overdue_reminder', label: 'Overdue Reminder' },
    { value: 'payment_failed_reminder', label: 'Payment Failed Reminder' },
];

const REMINDER_TYPE_LABELS: Record<string, string> = {
    'draft_incomplete_reminder': 'Draft Incomplete',
    'draft_completed_reminder': 'Draft Completed',
    'not_paid_reminder': 'Not Paid',
    'overdue_reminder': 'Overdue',
    'payment_failed_reminder': 'Payment Failed',
};

interface SubFilterOption {
    key: string;
    label: string;
    countKey: string;
    emailType?: string; // auto-set email type when clicked
}

const SUB_FILTERS: Partial<Record<PaymentFilterKey, SubFilterOption[]>> = {
    'paid': [
        { key: 'overdue-soon-7d', label: 'Expiring in 7 days', countKey: 'overdueSoon7dCount' },
        { key: 'overdue-soon-3d', label: 'Expiring in 3 days', countKey: 'overdueSoon3dCount' },
        { key: 'overdue-soon-1d', label: 'Expiring in 1 day', countKey: 'overdueSoon1dCount' },
        { key: 'subscription-overdue', label: 'Overdue', countKey: 'paidOverdueCount' },
    ],
    'never-paid': [
        { key: 'payment-failed', label: 'Payment Failed', countKey: 'paymentFailedCount', emailType: 'payment_failed_reminder' },
        { key: 'not-paid', label: 'Not Paid', countKey: 'notPaidUserCount', emailType: 'not_paid_reminder' },
        { key: 'draft-completed', label: 'Draft Completed', countKey: 'draftCompletedUserCount', emailType: 'draft_completed_reminder' },
        { key: 'draft-incomplete', label: 'Draft Incomplete', countKey: 'draftIncompleteUserCount', emailType: 'draft_incomplete_reminder' },
        { key: 'other', label: 'Other', countKey: 'otherNeverPaidCount' },
    ],
};

const Customers: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [currentLimit, setCurrentLimit] = useState<number>(10);
    const [searchField, setSearchField] = useState<string | undefined>();
    const [searchValue, setSearchValue] = useState<string | undefined>();
    const [selectedActivityLogs, setSelectedActivityLogs] = useState<string | undefined>();
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [paymentFilter, setPaymentFilter] = useState<PaymentFilterKey>('all');
    const [isDateModalOpen, setIsDateModalOpen] = useState(false);
    const [dateField, setDateField] = useState<string | undefined>();
    const [fromDate, setFromDate] = useState<string | undefined>();
    const [toDate, setToDate] = useState<string | undefined>();
    const [selectedEmailType, setSelectedEmailType] = useState<string | undefined>();
    const [subFilter, setSubFilter] = useState<string | undefined>();

    const {
        data: customerList,
        status,
        counts,
        filteredCount,
        paidCount,
        neverPaidCount,
        paymentFailedCount,
        overdueSoon7dCount,
        overdueSoon3dCount,
        overdueSoon1dCount,
        paidOverdueCount,
        overdueClaimUserCount,
        draftIncompleteUserCount,
        draftCompletedUserCount,
        notPaidUserCount,
        otherNeverPaidCount,
        refundedCount,
    } = useAppSelector(customerListDetail);

    const debouncedSearchValue = useDebounce(searchValue, 500);
    const loading = status === RequestResults.pending;

    const getFilterCount = (countKey: PaymentFilterOption['countKey']) => {
        const countMap = { counts, paidCount, neverPaidCount, refundedCount };
        return countMap[countKey] ?? 0;
    };

    const getSubFilterCount = (countKey: string) => {
        const countMap: Record<string, number> = {
            overdueSoon7dCount, overdueSoon3dCount, overdueSoon1dCount, paidOverdueCount,
            paymentFailedCount, draftIncompleteUserCount, draftCompletedUserCount,
            notPaidUserCount, overdueClaimUserCount, otherNeverPaidCount,
        };
        return countMap[countKey] ?? 0;
    };

    useEffect(() => {
        fetchCustomerList();
    }, [currentPage, currentLimit, debouncedSearchValue, searchField, selectedActivityLogs, fromDate, toDate, paymentFilter, subFilter]);

    const fetchCustomerList = async () => {
        const offset = (currentPage - 1) * currentLimit;

        const filters: any = {};

        if (debouncedSearchValue && searchField) {
            filters.searchValue = debouncedSearchValue;
            filters.searchField = searchField;
        }

        if (selectedActivityLogs) {
            filters.activityLogType = selectedActivityLogs;
        }

        if (fromDate && toDate) {
            filters.startDate = fromDate;
            filters.endDate = toDate;
        }

        filters.paymentStatus = paymentFilter;

        if (subFilter) {
            filters.subFilter = subFilter;
        }

        await dispatch(getCustomerDetails({ offset, limit: currentLimit, ...filters }));
    };

    const handleSearch = (value: string) => {
        setSearchValue(value);
        setCurrentPage(1);
    };

    const handleSearchFieldChange = (value: string | null) => {
        setSearchField(value || undefined);
        setCurrentPage(1);
    };

    const handleActivityLogsSearchFieldChange = (value: string | null) => {
        setSelectedActivityLogs(value || undefined);
        setCurrentPage(1);
    };

    const handleSendReminder = async () => {
        if (!selectedRowKeys.length || !selectedEmailType) return;

        try {
            message.loading({ content: 'Sending reminders...', key: 'sendReminder' });

            const result = await dispatch(sendCustomerReminder({
                customerIds: selectedRowKeys as string[],
                emailType: selectedEmailType
            }));

            if (sendCustomerReminder.fulfilled.match(result)) {
                message.success({
                    content: `Reminder sent to ${selectedRowKeys.length} customer${selectedRowKeys.length > 1 ? 's' : ''}`,
                    key: 'sendReminder',
                    duration: 3
                });

                setSelectedRowKeys([]);
                setSelectedEmailType(undefined);
                fetchCustomerList();
            } else {
                throw new Error(result.payload as string);
            }
        } catch (error: any) {
            message.error({
                content: error?.message || 'Failed to send reminders. Please try again.',
                key: 'sendReminder',
                duration: 3
            });
        }
    };

    const handleDateModalOk = (params: { dateField?: string; fromDate?: string; toDate?: string }) => {
        setDateField(params.dateField);
        setFromDate(params.fromDate);
        setToDate(params.toDate);
        setIsDateModalOpen(false);
        setCurrentPage(1);
    };

    const handleClearDateFilter = () => {
        setDateField(undefined);
        setFromDate(undefined);
        setToDate(undefined);
        setCurrentPage(1);
    };

    const handlePageChange = (page: number, pageSize: number) => {
        setCurrentPage(page);
        if (pageSize !== currentLimit) {
            setCurrentLimit(pageSize);
            setCurrentPage(1);
        }
    };

    const handleSelectAll = () => {
        setSelectedRowKeys(customerList.map((customer) => customer.id));
    };

    const handleDeselectAll = () => {
        setSelectedRowKeys([]);
    };

    const handleFilterChange = (filterKey: PaymentFilterKey) => {
        setPaymentFilter(filterKey);
        setSubFilter(undefined);
        setSelectedEmailType(undefined);
        setSelectedRowKeys([]);
        setCurrentPage(1);
    };

    const handleSubFilterChange = (sf: SubFilterOption) => {
        if (subFilter === sf.key) {
            // Toggle off
            setSubFilter(undefined);
            setSelectedEmailType(undefined);
        } else {
            setSubFilter(sf.key);
            if (sf.emailType) {
                setSelectedEmailType(sf.emailType);
            } else {
                setSelectedEmailType(undefined);
            }
        }
        setSelectedRowKeys([]);
        setCurrentPage(1);
    };

    const activityLogOptions = Object.entries(LostItemActivityLogText).map(([key, label]) => ({
        label,
        value: Number(key),
    }));

    const isDateRangeSelected = useMemo(() => !!(fromDate && toDate), [fromDate, toDate]);

    const formattedDateRange = useMemo(() => {
        return fromDate && toDate
            ? `${dayjs(fromDate).format("MM/DD/YYYY")} - ${dayjs(toDate).format("MM/DD/YYYY")}`
            : "Date Range";
    }, [fromDate, toDate]);

    const columns = [
        {
            title: "Customer",
            dataIndex: "firstName",
            key: "name",
            width: '22%',
            render: (_: any, record: any) => (
                <div style={{ minWidth: 0 }}>
                    <div>
                        <Text strong>
                            {record.firstName} {record.lastName}
                        </Text>
                    </div>
                    <Text type="secondary" style={{ fontSize: 12 }}>{record.email}</Text>
                    {record.phone && (
                        <>
                            <br />
                            <Text type="secondary" style={{ fontSize: 12 }}>{record.phone}</Text>
                        </>
                    )}
                    <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {record.paidClaimCount > 0 && !record.hasOverduePayment && (
                            <Tag color="success" style={{ fontSize: 10, margin: 0 }}>Paid: {record.paidClaimCount}</Tag>
                        )}
                        {record.paidClaimCount > 0 && record.hasOverduePayment && (
                            <Tag color="error" style={{ fontSize: 10, margin: 0 }}>Overdue: {record.paidClaimCount}</Tag>
                        )}
                        {record.notPaidCount > 0 && (
                            <Tag color="orange" style={{ fontSize: 10, margin: 0 }}>Never Paid: {record.notPaidCount}</Tag>
                        )}
                        {record.refundedClaimCount > 0 && (
                            <Tag color="red" style={{ fontSize: 10, margin: 0 }}>Refunded Payments: {record.refundedClaimCount}</Tag>
                        )}
                    </div>
                </div>
            ),
        },
        {
            title: "Claims",
            dataIndex: "claimsWithStatus",
            key: "claims",
            width: '18%',
            render: (claims: { trackingId: string; status: number }[], record: any) => {
                const items = claims || [];
                const total = items.length;

                if (total === 0) {
                    return <Tag color="blue">0 Claims</Tag>;
                }

                return (
                    <div>
                        <Tag color="blue">{total} Claim{total > 1 ? 's' : ''}</Tag>
                        <div style={{ marginTop: 4 }}>
                            {items.map((claim, index) => {
                                const theme = getStatusTheme(claim.status);
                                return (
                                    <div key={claim.trackingId} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
                                        <Text
                                            style={{
                                                fontSize: 12,
                                                color: "#1890ff",
                                                cursor: "pointer",
                                                textDecoration: "underline"
                                            }}
                                            onClick={() => navigate(`${SystemPayRoutes.reportBase}/${claim.trackingId}`)}
                                        >
                                            {claim.trackingId}
                                        </Text>
                                        <Tag style={{
                                            fontSize: 10,
                                            margin: 0,
                                            lineHeight: '16px',
                                            color: theme.pillColor,
                                            background: theme.pillBackground,
                                            border: `1px solid ${theme.border}`,
                                        }}>
                                            {theme.label}
                                        </Tag>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            },
        },
        {
            title: "Establishment",
            dataIndex: "establishmentTypes",
            key: "establishment",
            width: '12%',
            render: (types: string[]) => {
                if (!types?.length) {
                    return <Text type="secondary">-</Text>;
                }
                return (
                    <div>
                        {types.map((type, index) => (
                            <Tag key={index} color="cyan" style={{ fontSize: 11, marginBottom: 2 }}>
                                {type}
                            </Tag>
                        ))}
                    </div>
                );
            },
        },
        {
            title: "Subscription",
            dataIndex: "subscriptionType",
            key: "subscription",
            width: '15%',
            render: (_: any, record: any) => {
                const hasPaid = record.hasPaid;
                if (!hasPaid) {
                    return <Text type="secondary">-</Text>;
                }

                const subscriptionType = record.subscriptionType;
                const startDate = record.subscriptionStartDate;
                const expireDate = record.subscriptionExpireDate;
                const daysToExpire = record.daysToExpire;
                const isExpired = daysToExpire !== null && daysToExpire <= 0;
                const isExpiringSoon = daysToExpire !== null && daysToExpire > 0 && daysToExpire <= 30;

                return (
                    <div>
                        {subscriptionType && (
                            <Tag color="purple" style={{ marginBottom: 4 }}>
                                {subscriptionType}
                            </Tag>
                        )}
                        {startDate && (
                            <div>
                                <Text type="secondary" style={{ fontSize: 11 }}>
                                    Start: {dayjs(startDate).format("MM/DD/YYYY")}
                                </Text>
                            </div>
                        )}
                        {expireDate && (
                            <div>
                                <Text type="secondary" style={{ fontSize: 11 }}>
                                    Expire: {dayjs(expireDate).format("MM/DD/YYYY")}
                                </Text>
                            </div>
                        )}
                        {daysToExpire !== null && (
                            <Tag
                                color={isExpired ? 'red' : isExpiringSoon ? 'orange' : 'green'}
                                style={{ marginTop: 4, fontSize: 10 }}
                            >
                                {isExpired
                                    ? `Expired ${Math.abs(Math.floor(daysToExpire))} days ago`
                                    : `${Math.ceil(daysToExpire)} day${Math.ceil(daysToExpire) === 1 ? '' : 's'} left`
                                }
                            </Tag>
                        )}
                    </div>
                );
            },
        },
        {
            title: "Customer Since",
            dataIndex: "createdAt",
            key: "createdAt",
            width: '12%',
            render: (date: string) => (
                <div>
                    <Text>{dayjs(date).format("MM/DD/YYYY")}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {dayjs(date).fromNow()}
                    </Text>
                </div>
            ),
        },
        {
            title: "Last Reminder",
            dataIndex: "lastReminderAt",
            key: "lastReminderAt",
            width: '15%',
            render: (_: any, record: any) => (
                <div>
                    {record.lastReminderAt ? (
                        <>
                            <Text>{dayjs(record.lastReminderAt).format("MM/DD/YYYY")}</Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {dayjs(record.lastReminderAt).fromNow()}
                            </Text>
                            {record.lastReminderType && (
                                <div style={{ marginTop: 4 }}>
                                    <Tag color="geekblue" style={{ fontSize: 10 }}>
                                        {REMINDER_TYPE_LABELS[record.lastReminderType] || record.lastReminderType}
                                    </Tag>
                                </div>
                            )}
                        </>
                    ) : (
                        <Text type="secondary">Never Sent</Text>
                    )}
                </div>
            ),
        },
    ];

    return (
        <Container>
            <FilterTabWrapper>
                <StatusFilters>
                    {paymentFilterOptions.map((filter) => {
                        const isActive = paymentFilter === filter.key;
                        const countValue = getFilterCount(filter.countKey);
                        const chipTextColor = filter.colors.text;
                        const chipBackground = filter.colors.background;

                        return (
                            <FilterButton
                                key={filter.key}
                                active={isActive}
                                onClick={() => handleFilterChange(filter.key)}
                                $backgroundColor={filter.colors.background}
                                $borderColor={filter.colors.border}
                                $textColor={filter.colors.text}
                            >
                                {isActive && <CheckCircleFilled style={{ marginRight: 4, color: chipBackground }} />}
                                <span style={{ fontSize: "14px", fontWeight: 600 }}>{filter.label}</span>
                                <CountBadge $textColor={isActive ? "#ffffff" : chipTextColor} className="status-count-badge">
                                    {countValue}
                                </CountBadge>
                            </FilterButton>
                        );
                    })}
                </StatusFilters>

                <FilterInputs>
                    <StyledSelect
                        allowClear
                        value={searchField}
                        options={CustomersOptions}
                        onChange={(value) => handleSearchFieldChange(value as string | null)}
                        placeholder="Filter by"
                        size="middle"
                    />
                    {searchField === ActivityLogTypeValue ? (
                        <Select
                            style={{ minWidth: 200 }}
                            options={activityLogOptions}
                            allowClear
                            onChange={(value) => handleActivityLogsSearchFieldChange(value as string | null)}
                            value={selectedActivityLogs}
                            placeholder="Select activity type"
                        />
                    ) : (
                        <Input
                            placeholder="Search"
                            prefix={<SearchOutlined />}
                            value={searchValue}
                            onChange={(e) => handleSearch(e.target.value)}
                            allowClear
                            style={{ width: 200 }}
                        />
                    )}
                    <Tooltip title={isDateRangeSelected ? formattedDateRange : "Select date range"}>
                        <Button
                            icon={<CalendarOutlined />}
                            onClick={() => setIsDateModalOpen(true)}
                            type={isDateRangeSelected ? "primary" : "default"}
                            style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4, height: 32, minWidth: 32, padding: "0 8px" }}
                        >
                            {isDateRangeSelected && (
                                <CloseCircleOutlined
                                    style={{ marginLeft: 4 }}
                                    onClick={(e) => { e.stopPropagation(); handleClearDateFilter(); }}
                                />
                            )}
                        </Button>
                    </Tooltip>
                </FilterInputs>
            </FilterTabWrapper>

            {SUB_FILTERS[paymentFilter] && (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 16px',
                    marginBottom: 16,
                    background: '#f9fafb',
                    borderRadius: 8,
                    border: '1px solid #e5e7eb',
                }}>
                    <Text type="secondary" style={{ fontSize: 12, marginRight: 4 }}>Filter:</Text>
                    {SUB_FILTERS[paymentFilter]!.map((sf) => {
                        const isActive = subFilter === sf.key;
                        const count = getSubFilterCount(sf.countKey);
                        return (
                            <Tag
                                key={sf.key}
                                color={isActive ? 'blue' : undefined}
                                style={{
                                    cursor: 'pointer',
                                    fontSize: 13,
                                    padding: '4px 12px',
                                    borderRadius: 16,
                                    fontWeight: isActive ? 600 : 400,
                                }}
                                onClick={() => handleSubFilterChange(sf)}
                            >
                                {sf.label} ({count})
                            </Tag>
                        );
                    })}
                </div>
            )}

            <Card style={{ borderRadius: 10 }}>
                <div style={{ marginBottom: 24 }}>
                    <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                        <Col>
                            <Text strong style={{ fontSize: 20, display: 'block' }}>
                                Customers
                            </Text>
                        </Col>
                        <Col>
                            <Space size="middle">
                                <Button
                                    onClick={handleDeselectAll}
                                    disabled={!selectedRowKeys.length}
                                >
                                    Deselect All
                                </Button>
                                <Button
                                    onClick={handleSelectAll}
                                    disabled={!customerList.length}
                                >
                                    Select All
                                </Button>
                                {selectedRowKeys.length > 0 && (
                                    <>
                                        <Select
                                            style={{ minWidth: 220 }}
                                            placeholder="Select email type"
                                            value={selectedEmailType}
                                            onChange={(value) => setSelectedEmailType(value)}
                                            options={EMAIL_TYPE_OPTIONS}
                                            allowClear
                                        />
                                        <Button
                                            type="primary"
                                            icon={<MailOutlined />}
                                            disabled={!selectedEmailType}
                                            onClick={handleSendReminder}
                                        >
                                            Send ({selectedRowKeys.length})
                                        </Button>
                                    </>
                                )}
                            </Space>
                        </Col>
                    </Row>
                </div>

                <Table
                    rowKey="id"
                    rowSelection={{
                        selectedRowKeys,
                        onChange: (keys) => setSelectedRowKeys(keys),
                    }}
                    columns={columns}
                    dataSource={customerList}
                    pagination={false}
                    loading={loading}
                />

                <Row justify="space-between" align="middle" style={{ marginTop: 16 }}>
                    <Col>
                        <Text type="secondary">
                            {filteredCount > 0 ? `Showing ${(currentPage - 1) * currentLimit + 1} – ${Math.min(currentPage * currentLimit, filteredCount)} of ${filteredCount} customers` : 'No customers found'}
                        </Text>
                    </Col>
                    <Col>
                        <Pagination
                            current={currentPage}
                            pageSize={currentLimit}
                            total={filteredCount}
                            showSizeChanger
                            pageSizeOptions={["5", "10", "20", "50"]}
                            onChange={handlePageChange}
                        />
                    </Col>
                </Row>
            </Card>

            <DatePickerModal
                isModalOpen={isDateModalOpen}
                handleOk={handleDateModalOk}
                handleCancel={() => setIsDateModalOpen(false)}
                values={{ dateField, fromDate, toDate }}
            />
        </Container>
    )
};

export default Customers;
